(() => {
  'use strict';

  const VERSION = '2.1.0';
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const ROOT_SELECTOR = '[data-rd-overlays]';
  const DESKTOP_X = [0.18, 0.31, 0.72, 0.84, 0.57, 0.23, 0.38, 0.79, 0.67, 0.27, 0.48, 0.82];
  const MOBILE_X = [0.18, 0.77, 0.70, 0.24, 0.31, 0.82, 0.68, 0.20];

  if (window.RawDelightsPawTrail?.version === VERSION) {
    window.RawDelightsPawTrail.init();
    return;
  }

  const debugEnabled = () => new URLSearchParams(location.search).get('rd_overlay_debug') === '1';
  const diagnostic = (message, details) => {
    if (!debugEnabled()) return;
    if (details === undefined) console.info(`[Raw Delights paw trail] ${message}`);
    else console.info(`[Raw Delights paw trail] ${message}`, details);
  };
  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
  const pageScrollY = () => Math.max(window.scrollY || 0, document.documentElement?.scrollTop || 0, document.body?.scrollTop || 0);
  const nextFrame = () => new Promise((resolve) => window.requestAnimationFrame(resolve));
  const interpolateAngle = (from, to, amount) => {
    const shortestDelta = ((to - from + 540) % 360) - 180;
    return from + shortestDelta * amount;
  };

  const createSvg = (name, attributes = {}) => {
    const element = document.createElementNS(SVG_NS, name);
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, String(value)));
    return element;
  };

  const hashString = (value) => {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  };

  const seededGenerator = (seed) => {
    let state = seed >>> 0;
    return () => {
      state += 0x6d2b79f5;
      let value = state;
      value = Math.imul(value ^ (value >>> 15), value | 1);
      value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
      return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };
  };

  const parseConfig = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    const node = root?.querySelector('[data-rd-overlays-config]');
    if (!root || !node) return null;
    try {
      return { root, config: JSON.parse(node.textContent) };
    } catch (error) {
      if (debugEnabled()) console.warn('[Raw Delights paw trail] Invalid configuration.', error);
      return null;
    }
  };

  const isDarkColor = (value) => {
    const parts = String(value).match(/[\d.]+/g);
    if (!parts || parts.length < 3) return false;
    if (parts.length > 3 && Number(parts[3]) === 0) return false;
    const [red, green, blue] = parts.slice(0, 3).map(Number);
    const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
    return luminance < 0.48;
  };

  const sectionIsDark = (section) => {
    const candidates = [
      section,
      section.firstElementChild,
      section.querySelector('[style*="background"], [class*="color-"]')
    ].filter(Boolean);
    for (const candidate of candidates) {
      const color = window.getComputedStyle(candidate).backgroundColor;
      const parts = String(color).match(/[\d.]+/g);
      if (parts && (parts.length < 4 || Number(parts[3]) > 0)) return isDarkColor(color);
    }
    return false;
  };

  class PawTrail {
    constructor(root, config) {
      this.root = root;
      this.config = config;
      this.settings = config.paws || {};
      this.main = null;
      this.mainSelector = 'not found';
      this.layer = null;
      this.svg = null;
      this.revealRect = null;
      this.currentPaw = null;
      this.stepPaws = [];
      this.revealedStepCount = 0;
      this.routePoints = [];
      this.routeStart = 0;
      this.routeEnd = 0;
      this.mainDocumentTop = 0;
      this.viewportHeight = window.innerHeight;
      this.maximumProgressReached = 0;
      this.pawCount = 0;
      this.buildDuration = 0;
      this.rebuildCount = 0;
      this.status = 'scheduled';
      this.active = false;
      this.destroyed = false;
      this.building = false;
      this.rebuildRequested = false;
      this.scrollFrame = 0;
      this.motionFrame = 0;
      this.targetProgress = 0;
      this.renderedProgress = 0;
      this.lastMotionTime = 0;
      this.progressSyncTimer = 0;
      this.lastObservedScrollY = pageScrollY();
      this.lastObservedMainTop = 0;
      this.lastLoggedProgress = -1;
      this.resizeTimer = 0;
      this.idleHandle = 0;
      this.fallbackTimer = 0;
      this.resizeObserver = null;
      this.lastWidth = 0;
      this.lastHeight = 0;
      this.lastMobile = false;
      this.activationListeners = [];
      this.boundActivate = (event) => {
        if (event.isTrusted) this.activate();
      };
      this.boundScroll = () => this.onScroll();
      this.boundLoad = () => this.scheduleIdleActivation();
    }

    init() {
      if (this.destroyed || this.active || this.activationListeners.length) return;
      if (!this.settings.enabled) {
        this.status = 'disabled';
        diagnostic('Disabled by the paw-trail setting.');
        this.report();
        return;
      }
      diagnostic('Initialised and awaiting activation.', {
        desktopEnabled: this.settings.desktopEnabled !== false,
        mobileEnabled: this.settings.mobileEnabled !== false,
        startOffset: this.settings.startOffset,
        endOffset: this.settings.endOffset
      });
      // Paw rendering is independent of visitor engagement. Scheduling it at
      // idle avoids doing route geometry work inside a wheel/touch handler.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.boundLoad, { once: true });
      } else {
        this.scheduleIdleActivation();
      }
      this.report();
    }

    scheduleIdleActivation() {
      if (this.active || this.destroyed) return;
      if ('requestIdleCallback' in window) {
        this.idleHandle = window.requestIdleCallback(() => this.activate(), { timeout: 800 });
      } else {
        this.fallbackTimer = window.setTimeout(() => this.activate(), 800);
      }
    }

    removeActivationListeners() {
      this.activationListeners.splice(0).forEach((remove) => remove());
      window.removeEventListener('load', this.boundLoad);
      document.removeEventListener('DOMContentLoaded', this.boundLoad);
      if (this.idleHandle && 'cancelIdleCallback' in window) window.cancelIdleCallback(this.idleHandle);
      window.clearTimeout(this.fallbackTimer);
      this.idleHandle = 0;
      this.fallbackTimer = 0;
    }

    async activate() {
      if (this.active || this.destroyed) return;
      this.active = true;
      this.removeActivationListeners();
      diagnostic('Activation started.');
      await this.build();
    }

    resolveMain() {
      const override = document.querySelector('[data-rd-paw-root]');
      if (override) {
        this.mainSelector = '[data-rd-paw-root]';
        return override;
      }
      const main = document.querySelector('main, #MainContent, [role="main"]');
      this.mainSelector = main?.id === 'MainContent' ? '#MainContent' : main?.getAttribute('role') === 'main' ? '[role="main"]' : 'main';
      diagnostic('Resolved page-content root.', { selector: this.mainSelector, found: Boolean(main) });
      return main;
    }

    modeAllowed(mobile) {
      return mobile ? this.settings.mobileEnabled !== false : this.settings.desktopEnabled !== false;
    }

    relativeBottom(element, mainRect) {
      const rect = element.getBoundingClientRect();
      return rect.bottom - mainRect.top;
    }

    relativeTop(element, mainRect) {
      const rect = element.getBoundingClientRect();
      return rect.top - mainRect.top;
    }

    getRouteBounds(main, mainRect, height) {
      const startOffset = Number(this.settings.startOffset || 120);
      const endOffset = Number(this.settings.endOffset || 150);
      const explicitStart = main.querySelector('[data-rd-paw-start]');
      const hero = this.settings.startAfterHero !== false
        ? main.querySelector('[data-rd-hero], .banner, [class*="hero"], .slideshow')
        : null;
      const firstSection = main.querySelector(':scope > .shopify-section') || main.querySelector('.shopify-section');
      const explicitEnd = main.querySelector('[data-rd-paw-end]');

      let start = startOffset;
      if (explicitStart) start = this.relativeBottom(explicitStart, mainRect) + startOffset;
      else if (hero) start = this.relativeBottom(hero, mainRect) + startOffset;
      else if (firstSection) start = this.relativeBottom(firstSection, mainRect) + startOffset;

      let end = height - endOffset;
      if (explicitEnd) end = this.relativeTop(explicitEnd, mainRect) - endOffset;
      start = clamp(start, 0, Math.max(0, height - 320));
      end = clamp(end, start + 280, height);
      return { start, end };
    }

    createAnchors(width, start, end, mobile, random) {
      const sequence = mobile ? MOBILE_X : DESKTOP_X;
      const minimumSpacing = mobile ? 950 : 750;
      const maximumSpacing = mobile ? 1200 : 950;
      const anchors = [];
      let y = start;
      let index = 0;
      while (y < end) {
        const horizontal = clamp(sequence[index % sequence.length] + (random() - 0.5) * 0.04, mobile ? 0.14 : 0.12, mobile ? 0.86 : 0.88);
        anchors.push({ x: width * horizontal, y: Math.min(y, end) });
        y += minimumSpacing + random() * (maximumSpacing - minimumSpacing);
        index += 1;
      }
      const finalX = clamp(sequence[index % sequence.length] + (random() - 0.5) * 0.04, mobile ? 0.14 : 0.12, mobile ? 0.86 : 0.88);
      if (!anchors.length || anchors[anchors.length - 1].y < end) anchors.push({ x: width * finalX, y: end });
      if (anchors.length === 1) anchors.unshift({ x: width * sequence[0], y: start });
      return anchors;
    }

    anchorsToPath(anchors) {
      let path = `M ${anchors[0].x.toFixed(2)} ${anchors[0].y.toFixed(2)}`;
      for (let index = 0; index < anchors.length - 1; index += 1) {
        const previous = anchors[Math.max(0, index - 1)];
        const current = anchors[index];
        const next = anchors[index + 1];
        const after = anchors[Math.min(anchors.length - 1, index + 2)];
        const controlOne = {
          x: current.x + (next.x - previous.x) / 6,
          y: current.y + (next.y - previous.y) / 6
        };
        const controlTwo = {
          x: next.x - (after.x - current.x) / 6,
          y: next.y - (after.y - current.y) / 6
        };
        path += ` C ${controlOne.x.toFixed(2)} ${controlOne.y.toFixed(2)}, ${controlTwo.x.toFixed(2)} ${controlTwo.y.toFixed(2)}, ${next.x.toFixed(2)} ${next.y.toFixed(2)}`;
      }
      return path;
    }

    classifySections(main, mainRect) {
      return Array.from(main.querySelectorAll(':scope > .shopify-section')).map((section) => {
        const rect = section.getBoundingClientRect();
        return {
          top: rect.top - mainRect.top,
          bottom: rect.bottom - mainRect.top,
          dark: sectionIsDark(section)
        };
      });
    }

    sectionIsDark(y, sections) {
      for (const section of sections) {
        if (y >= section.top && y <= section.bottom) return section.dark;
      }
      return false;
    }

    makePawUse(symbolId, point, size, angle, dark, current = false) {
      const color = current
        ? (this.settings.currentColor || '#B9684B')
        : dark
          ? (this.settings.darkColor || '#F7F3EA')
          : (this.settings.lightColor || '#24382E');
      const opacity = current
        ? Math.max(0.48, Number(this.settings.currentOpacity ?? 0.48))
        : Math.max(0.18, Number(this.settings.completedOpacity ?? 0.18));
      const scale = size / 64;
      const paw = createSvg('g', {
        transform: `translate(${point.x.toFixed(2)} ${point.y.toFixed(2)}) rotate(${angle.toFixed(2)}) scale(${scale.toFixed(4)}) translate(-32 -32)`,
        fill: color,
        opacity: opacity.toFixed(3)
      });
      paw.append(
        createSvg('ellipse', { cx: 32, cy: 41, rx: 14, ry: 12 }),
        createSvg('ellipse', { cx: 15, cy: 26, rx: 7, ry: 9, transform: 'rotate(-24 15 26)' }),
        createSvg('ellipse', { cx: 27, cy: 15, rx: 7, ry: 9, transform: 'rotate(-8 27 15)' }),
        createSvg('ellipse', { cx: 41, cy: 15, rx: 7, ry: 9, transform: 'rotate(8 41 15)' }),
        createSvg('ellipse', { cx: 52, cy: 26, rx: 7, ry: 9, transform: 'rotate(24 52 26)' })
      );
      paw.setAttribute('class', current ? 'rd-paw-current' : `rd-paw-step${dark ? ' rd-paw-step--dark' : ''}`);
      paw.style.setProperty('display', 'block', 'important');
      paw.style.setProperty('fill', color, 'important');
      paw.style.setProperty('color', color, 'important');
      paw.style.setProperty('opacity', opacity.toFixed(3), 'important');
      paw.style.setProperty('visibility', current ? 'visible' : 'hidden', 'important');
      paw.dataset.rdPawScale = scale.toFixed(4);
      if (current) paw.setAttribute('data-rd-paw-current', '');
      return paw;
    }

    async build() {
      if (this.building || this.destroyed) {
        this.rebuildRequested = this.building;
        return;
      }
      this.building = true;
      this.status = 'building';
      if (this.motionFrame) window.cancelAnimationFrame(this.motionFrame);
      this.motionFrame = 0;
      this.report();
      const startedAt = performance.now();

      try {
        this.resizeObserver?.disconnect();
        this.layer?.remove();
        this.layer = null;
        this.main?.classList.remove('rd-paw-host');

        const main = this.resolveMain();
        if (!(main instanceof HTMLElement)) throw new Error('Main content wrapper was not found.');
        const cssReady = window.getComputedStyle(document.documentElement).getPropertyValue('--rd-paw-css-ready').trim() === '1';
        if (!cssReady) throw new Error('Paw trail stylesheet was not loaded.');
        const mobile = window.matchMedia('(max-width: 748px)').matches;
        if (!this.modeAllowed(mobile)) {
          this.main = main;
          this.lastWidth = Math.max(main.clientWidth, 1);
          this.lastHeight = Math.max(main.scrollHeight, main.clientHeight, 1);
          this.lastMobile = mobile;
          this.status = mobile ? 'disabled-mobile' : 'disabled-desktop';
          this.attachResizeObserver();
          return;
        }

        const width = Math.max(main.clientWidth, 1);
        const height = Math.max(main.scrollHeight, main.clientHeight, 1);
        diagnostic('Building route.', { selector: this.mainSelector, width, height, mobile });
        const mainRect = main.getBoundingClientRect();
        const bounds = this.getRouteBounds(main, mainRect, height);
        if (bounds.end - bounds.start < 280) throw new Error('The main content area is too short for the configured trail.');

        const seed = hashString(`${location.pathname}|${width}|${height}|${mobile ? 'mobile' : 'desktop'}`);
        const random = seededGenerator(seed);
        const anchors = this.createAnchors(width, bounds.start, bounds.end, mobile, random);
        const routeData = this.anchorsToPath(anchors);
        const unique = `${String(this.config.sectionId).replace(/[^a-z0-9_-]/gi, '')}-${this.rebuildCount}`;
        const symbolId = `rd-paw-symbol-${unique}`;
        const clipId = `rd-paw-reveal-${unique}`;

        const layer = document.createElement('div');
        layer.className = `rd-paw-trail${this.settings.transitions === false ? ' is-static' : ''}`;
        layer.dataset.rdPawTrail = '';
        layer.setAttribute('aria-hidden', 'true');
        layer.style.position = 'absolute';
        layer.style.inset = '0';
        layer.style.width = '100%';
        layer.style.height = '100%';
        layer.style.overflow = 'hidden';
        layer.style.pointerEvents = 'none';
        // The layer must sit above opaque Shopify section backgrounds while
        // remaining non-interactive, otherwise the correctly built SVG is hidden.
        layer.style.zIndex = '2';
        layer.style.setProperty('--rd-paw-light', this.settings.lightColor || '#24382E');
        layer.style.setProperty('--rd-paw-dark', this.settings.darkColor || '#F7F3EA');
        layer.style.setProperty('--rd-paw-current', this.settings.currentColor || '#B9684B');
        layer.style.setProperty('--rd-paw-completed-opacity', String(Math.max(0.18, Number(this.settings.completedOpacity ?? 0.18))));
        layer.style.setProperty('--rd-paw-current-opacity', String(Math.max(0.48, Number(this.settings.currentOpacity ?? 0.48))));
        // The route is a deliberately subtle visual guide. Keep it available for
        // existing section instances whose previous saved setting was "off".
        layer.style.setProperty('--rd-paw-route-opacity', String(Math.max(0.08, Number(this.settings.routeOpacity ?? 0.08))));

        const svg = createSvg('svg', {
          class: 'rd-paw-trail__svg',
          'data-rd-paw-svg': '',
          viewBox: `0 0 ${width} ${height}`,
          preserveAspectRatio: 'none',
          focusable: 'false',
          'aria-hidden': 'true'
        });
        const defs = createSvg('defs');
        const symbol = createSvg('symbol', { id: symbolId, viewBox: '0 0 64 64' });
        symbol.append(
          createSvg('ellipse', { cx: 32, cy: 41, rx: 14, ry: 12 }),
          createSvg('ellipse', { cx: 15, cy: 26, rx: 7, ry: 9, transform: 'rotate(-24 15 26)' }),
          createSvg('ellipse', { cx: 27, cy: 15, rx: 7, ry: 9, transform: 'rotate(-8 27 15)' }),
          createSvg('ellipse', { cx: 41, cy: 15, rx: 7, ry: 9, transform: 'rotate(8 41 15)' }),
          createSvg('ellipse', { cx: 52, cy: 26, rx: 7, ry: 9, transform: 'rotate(24 52 26)' })
        );
        const clipPath = createSvg('clipPath', { id: clipId, clipPathUnits: 'userSpaceOnUse' });
        const revealRect = createSvg('rect', { x: 0, y: 0, width, height: 0, 'data-rd-paw-reveal': '' });
        clipPath.appendChild(revealRect);
        defs.append(symbol, clipPath);
        const route = createSvg('path', { class: 'rd-paw-route', d: routeData });
        const steps = createSvg('g', { 'data-rd-paw-steps': '' });
        svg.append(defs, route, steps);
        layer.appendChild(svg);
        main.insertBefore(layer, main.firstChild);
        main.classList.add('rd-paw-host');

        const routeLength = route.getTotalLength();
        const minCount = Number(mobile ? this.settings.mobileMinCount : this.settings.desktopMinCount);
        const maxCount = Number(mobile ? this.settings.mobileMaxCount : this.settings.desktopMaxCount);
        const requestedMinimum = Math.min(minCount, maxCount);
        const requestedMaximum = Math.max(minCount, maxCount);
        // Preserve the merchant's chosen range while ensuring the route reads
        // as footsteps rather than a handful of widely spaced markers.
        const densityMinimum = mobile ? 28 : 52;
        const densityMaximum = mobile ? 42 : 72;
        const pawCount = clamp(
          Math.round(routeLength / (mobile ? 245 : 155)),
          Math.max(requestedMinimum, densityMinimum),
          Math.max(requestedMaximum, densityMaximum)
        );
        const minSize = Number(mobile ? this.settings.mobileMinSize : this.settings.desktopMinSize);
        const maxSize = Number(mobile ? this.settings.mobileMaxSize : this.settings.desktopMaxSize);
        const sections = this.classifySections(main, mainRect);
        const pawPlans = [];

        for (let index = 0; index < pawCount; index += 1) {
          const distance = routeLength * ((index + 1) / (pawCount + 1));
          const point = route.getPointAtLength(distance);
          const before = route.getPointAtLength(Math.max(0, distance - 3));
          const after = route.getPointAtLength(Math.min(routeLength, distance + 3));
          const deltaX = after.x - before.x;
          const deltaY = after.y - before.y;
          const magnitude = Math.max(0.001, Math.hypot(deltaX, deltaY));
          const lateralMagnitude = 8 + random() * 5;
          const lateral = index % 2 === 0 ? -lateralMagnitude : lateralMagnitude;
          const shifted = {
            x: point.x + (-deltaY / magnitude) * lateral,
            y: point.y + (deltaX / magnitude) * lateral
          };
          const footRotation = index % 2 === 0 ? -(6 + random() * 5) : 6 + random() * 5;
          const angle = Math.atan2(deltaY, deltaX) * 180 / Math.PI + 90 + footRotation;
          const size = Math.min(minSize, maxSize) + random() * Math.abs(maxSize - minSize);
          pawPlans.push({
            point: shifted,
            size,
            angle,
            dark: this.sectionIsDark(point.y, sections)
          });
        }

        const sampleCount = 240;
        const routePoints = [];
        for (let index = 0; index <= sampleCount; index += 1) {
          const distance = routeLength * (index / sampleCount);
          const point = route.getPointAtLength(distance);
          const after = route.getPointAtLength(Math.min(routeLength, distance + 3));
          routePoints.push({
            x: point.x,
            y: point.y,
            angle: Math.atan2(after.y - point.y, after.x - point.x) * 180 / Math.PI + 90
          });
        }

        const stepPaws = [];
        for (let index = 0; index < pawPlans.length; index += 8) {
          const fragment = document.createDocumentFragment();
          pawPlans.slice(index, index + 8).forEach((plan) => {
            const paw = this.makePawUse(symbolId, plan.point, plan.size, plan.angle, plan.dark);
            stepPaws.push(paw);
            fragment.appendChild(paw);
          });
          steps.appendChild(fragment);
          if (index + 8 < pawPlans.length) await nextFrame();
          if (this.destroyed) return;
        }

        await nextFrame();
        if (this.destroyed) return;
        const currentSize = (Math.min(minSize, maxSize) + Math.max(minSize, maxSize)) / 2;
        const currentPaw = this.makePawUse(symbolId, routePoints[0], currentSize, routePoints[0].angle, false, true);
        svg.appendChild(currentPaw);

        this.main = main;
        this.layer = layer;
        this.svg = svg;
        this.revealRect = revealRect;
        this.currentPaw = currentPaw;
        this.stepPaws = stepPaws;
        this.revealedStepCount = 0;
        this.maximumProgressReached = 0;
        this.targetProgress = 0;
        this.renderedProgress = 0;
        this.lastMotionTime = 0;
        this.routePoints = routePoints;
        this.routeStart = bounds.start;
        this.routeEnd = bounds.end;
        this.mainDocumentTop = mainRect.top + pageScrollY();
        this.viewportHeight = window.innerHeight;
        this.pawCount = pawCount;
        this.lastWidth = width;
        this.lastHeight = height;
        this.lastMobile = mobile;
        this.buildDuration = performance.now() - startedAt;
        this.status = 'ready';
        layer.classList.add('is-ready');
        this.attachScroll();
        this.attachResizeObserver();
        this.update(true);
        diagnostic('Trail ready.', { pawCount: this.pawCount, routeStart: this.routeStart, routeEnd: this.routeEnd, buildDuration: Math.round(this.buildDuration) });
      } catch (error) {
        this.status = 'error';
        this.layer?.remove();
        this.main?.classList.remove('rd-paw-host');
        this.layer = null;
        if (debugEnabled()) console.error('[Raw Delights paw trail] Build failed.', error);
      } finally {
        this.building = false;
        this.report();
        if (this.rebuildRequested && !this.destroyed) {
          this.rebuildRequested = false;
          this.rebuild();
        }
      }
    }

    attachScroll() {
      window.removeEventListener('scroll', this.boundScroll);
      document.removeEventListener('scroll', this.boundScroll, true);
      window.visualViewport?.removeEventListener('scroll', this.boundScroll);
      window.addEventListener('scroll', this.boundScroll, { passive: true });
      // Some Shopify themes scroll a document wrapper rather than window.
      // Capture document scrolls as well so reveal progression remains live.
      document.addEventListener('scroll', this.boundScroll, { passive: true, capture: true });
      window.visualViewport?.addEventListener('scroll', this.boundScroll, { passive: true });
      window.clearInterval(this.progressSyncTimer);
      this.lastObservedScrollY = pageScrollY();
      this.lastObservedMainTop = this.main?.getBoundingClientRect().top || 0;
      // Fallback for themes that animate scrolling without dispatching a
      // reliable window scroll event. This only changes two SVG attributes.
      this.progressSyncTimer = window.setInterval(() => {
        if (document.visibilityState !== 'visible' || this.status !== 'ready') return;
        const currentScrollY = pageScrollY();
        const currentMainTop = this.main?.getBoundingClientRect().top || 0;
        if (Math.abs(currentScrollY - this.lastObservedScrollY) < 1 && Math.abs(currentMainTop - this.lastObservedMainTop) < 0.5) return;
        this.lastObservedScrollY = currentScrollY;
        this.lastObservedMainTop = currentMainTop;
        this.update();
      }, 120);
    }

    onScroll() {
      if (this.scrollFrame || this.status !== 'ready') return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.lastObservedScrollY = pageScrollY();
        this.lastObservedMainTop = this.main?.getBoundingClientRect().top || 0;
        this.update();
      });
    }

    shouldSmoothMobileMotion() {
      return this.lastMobile && this.settings.transitions !== false && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    getScrollProgress() {
      const mainViewportTop = this.main?.getBoundingClientRect().top || 0;
      const referenceY = window.innerHeight * 0.62;
      const routeStartViewportY = mainViewportTop + this.routeStart;
      return clamp((referenceY - routeStartViewportY) / Math.max(1, this.routeEnd - this.routeStart), 0, 1);
    }

    update(immediate = false) {
      if (this.status !== 'ready' || !this.currentPaw || !this.routePoints.length) return;
      this.targetProgress = this.getScrollProgress();

      if (immediate || !this.shouldSmoothMobileMotion()) {
        if (this.motionFrame) window.cancelAnimationFrame(this.motionFrame);
        this.motionFrame = 0;
        this.renderedProgress = this.targetProgress;
        this.renderProgress(this.renderedProgress);
        return;
      }

      if (!Number.isFinite(this.renderedProgress)) this.renderedProgress = this.targetProgress;
      if (this.motionFrame) return;
      this.lastMotionTime = performance.now();
      this.motionFrame = window.requestAnimationFrame((timestamp) => this.animateProgress(timestamp));
    }

    animateProgress(timestamp) {
      this.motionFrame = 0;
      if (this.status !== 'ready' || !this.currentPaw || !this.routePoints.length) return;

      if (!this.shouldSmoothMobileMotion()) {
        this.renderedProgress = this.targetProgress;
        this.renderProgress(this.renderedProgress);
        return;
      }

      const elapsed = Math.min(64, Math.max(0, timestamp - this.lastMotionTime));
      this.lastMotionTime = timestamp;
      const easing = 1 - Math.exp(-elapsed / 85);
      this.renderedProgress += (this.targetProgress - this.renderedProgress) * easing;

      if (Math.abs(this.targetProgress - this.renderedProgress) < 0.00025) {
        this.renderedProgress = this.targetProgress;
      }

      this.renderProgress(this.renderedProgress);
      if (Math.abs(this.targetProgress - this.renderedProgress) > 0.0001) {
        this.motionFrame = window.requestAnimationFrame((nextTimestamp) => this.animateProgress(nextTimestamp));
      }
    }

    renderProgress(progress) {
      const mainViewportTop = this.main?.getBoundingClientRect().top || 0;
      const renderedProgress = clamp(progress, 0, 1);
      this.maximumProgressReached = Math.max(this.maximumProgressReached, renderedProgress);
      const revealHeight = this.routeStart + (this.routeEnd - this.routeStart) * this.maximumProgressReached;
      const scaled = renderedProgress * (this.routePoints.length - 1);
      const lowerIndex = Math.floor(scaled);
      const upperIndex = Math.min(this.routePoints.length - 1, lowerIndex + 1);
      const amount = scaled - lowerIndex;
      const lower = this.routePoints[lowerIndex];
      const upper = this.routePoints[upperIndex];
      const x = lower.x + (upper.x - lower.x) * amount;
      const y = lower.y + (upper.y - lower.y) * amount;
      const angle = interpolateAngle(lower.angle, upper.angle, amount);
      this.revealRect?.setAttribute('height', revealHeight.toFixed(2));
      const currentScale = this.currentPaw.dataset.rdPawScale || '1';
      this.currentPaw.setAttribute('transform', `translate(${x.toFixed(2)} ${y.toFixed(2)}) rotate(${angle.toFixed(2)}) scale(${currentScale}) translate(-32 -32)`);
      const targetStepCount = Math.min(this.stepPaws.length, Math.ceil(this.maximumProgressReached * this.stepPaws.length));
      while (this.revealedStepCount < targetStepCount) {
        this.stepPaws[this.revealedStepCount].style.setProperty('visibility', 'visible', 'important');
        this.revealedStepCount += 1;
      }
      if (debugEnabled() && renderedProgress - this.lastLoggedProgress >= 0.05) {
        this.lastLoggedProgress = renderedProgress;
        diagnostic('Reveal updated.', { mainTop: Math.round(mainViewportTop), progress: Number(renderedProgress.toFixed(3)), revealHeight: Math.round(revealHeight) });
      }
    }

    attachResizeObserver() {
      if (!('ResizeObserver' in window) || !this.main) return;
      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry || this.building) return;
        const width = entry.contentRect.width;
        const height = entry.contentRect.height;
        const mobile = width <= 748;
        const breakpointChanged = mobile !== this.lastMobile;
        if (!breakpointChanged && Math.abs(width - this.lastWidth) < 32 && Math.abs(height - this.lastHeight) < 220) return;
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.rebuild(), 700);
      });
      this.resizeObserver.observe(this.main);
    }

    rebuild() {
      if (this.destroyed || !this.active) return;
      if (this.building) {
        this.rebuildRequested = true;
        return;
      }
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = 0;
      this.rebuildCount += 1;
      this.build();
    }

    report() {
      document.dispatchEvent(new CustomEvent('rd:paw-status', { detail: this.getStatus() }));
    }

    getStatus() {
      return {
        status: this.status,
        pawCount: this.pawCount,
        buildDuration: this.buildDuration,
        rebuildCount: this.rebuildCount,
        mainSelector: this.mainSelector
      };
    }

    destroy() {
      this.destroyed = true;
      this.removeActivationListeners();
      window.removeEventListener('scroll', this.boundScroll);
      document.removeEventListener('scroll', this.boundScroll, true);
      window.visualViewport?.removeEventListener('scroll', this.boundScroll);
      window.clearInterval(this.progressSyncTimer);
      this.progressSyncTimer = 0;
      window.clearTimeout(this.resizeTimer);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      if (this.motionFrame) window.cancelAnimationFrame(this.motionFrame);
      this.motionFrame = 0;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.layer?.remove();
      this.main?.classList.remove('rd-paw-host');
      this.layer = null;
      this.status = 'destroyed';
      this.report();
    }
  }

  let instance = null;

  const manager = {
    version: VERSION,
    init() {
      if (instance && !instance.destroyed) return manager;
      const resolved = parseConfig();
      if (!resolved) {
        diagnostic('No overlay root or configuration was found.');
        return manager;
      }
      if (!resolved.config.enabled) {
        diagnostic('Disabled because the parent overlay section is disabled.');
        return manager;
      }
      diagnostic('Module loaded.', { sectionId: resolved.config.sectionId, paws: resolved.config.paws });
      instance = new PawTrail(resolved.root, resolved.config);
      instance.init();
      return manager;
    },
    destroy() {
      instance?.destroy();
      instance = null;
      return manager;
    },
    build() {
      if (!instance) manager.init();
      instance?.activate();
      return manager;
    },
    rebuild() {
      instance?.rebuild();
      return manager;
    },
    update() {
      instance?.update();
      return manager;
    },
    getStatus() {
      return instance?.getStatus() || { status: 'inactive', pawCount: 0, buildDuration: 0, rebuildCount: 0, mainSelector: 'not found' };
    }
  };

  window.RawDelightsPawTrail = manager;

  const start = () => manager.init();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  if (!window.__rdPawSectionListeners) {
    window.__rdPawSectionListeners = true;
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.querySelector?.(ROOT_SELECTOR) || event.target.matches?.(ROOT_SELECTOR)) {
        manager.destroy().init();
      } else {
        manager.rebuild();
      }
    });
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.querySelector?.(ROOT_SELECTOR) || event.target.matches?.(ROOT_SELECTOR)) manager.destroy();
      else manager.rebuild();
    });
    document.addEventListener('shopify:section:reorder', () => manager.rebuild());
  }
})();
